import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { login02, loginicon01, loginicon02, loginicon03, loginlogo } from "../../imagepath";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import OTPInput from "react-otp-input";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { auth } from "../../../firebase.config";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import config from "../../../config";

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [digitCount, setDigitCount] = useState(0);
  const [requiredDigits, setRequiredDigits] = useState(10);
  const [otp, setOtp] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [errorVal, setErrorVal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const navigate = useNavigate();

  // Timer countdown effect
  useEffect(() => {
    let countdown;
    if (isOtpSent && timer > 0) {
      countdown = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(countdown);
    }
    return () => clearInterval(countdown);
  }, [isOtpSent, timer]);

  // Auto-verify OTP when 6 digits are entered
  useEffect(() => {
    if (otp.length === 6 && !loading && !verificationAttempted) {
      verifyOtp({ preventDefault: () => {} });
    }
  }, [otp, loading, verificationAttempted]);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: () => console.log("Recaptcha resolved."),
      });
    }
  };

  const validatePhoneNumber = (phoneNumber, countryCode) => {
    const phoneLength = phoneNumber.length;
    switch (countryCode) {
      case "in":
      case "gb":
      case "us":
        return phoneLength === 10;
      case "ae":
        return phoneLength === 9;
      case "ng":
        return phoneLength === 11;
      case "lk":
        return phoneLength === 9;
      case "bd":
        return phoneLength === 10;
      default:
        return false;
    }
  };

  const getRequiredDigits = (countryCode) => {
    switch (countryCode) {
      case "in":
      case "gb":
      case "us":
      case "bd":
        return 10;
      case "ae":
      case "lk":
        return 9;
      case "ng":
        return 11;
      default:
        return 10;
    }
  };

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorVal(false);
    setupRecaptcha();

    const appVerifier = window.recaptchaVerifier;
    const formatPhoneNumber = "+" + phoneNumber;

    try {
      const confirmationResult = await signInWithPhoneNumber(auth, formatPhoneNumber, appVerifier);
      setVerificationId(confirmationResult.verificationId);
      setIsOtpSent(true);
      setTimer(60);
      setCanResend(false);
      setOtp("");
      toast.success("OTP sent successfully!");
    } catch (error) {
      console.error("Error sending OTP:", error);
      setErrorVal(true);
      toast.error(`Error sending OTP: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setVerificationAttempted(true);
    setErrorVal(false);

    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);
      await signInWithCredential(auth, credential);

      const phone = "+" + phoneNumber;

      const response = await fetch(`${config.API_URL}user_check3.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ phone: phone }),
      });

      const data = await response.json();
      console.log("API Response:", data);

      if (data.user_exists === true && data.user_id && data.token) {
        localStorage.setItem("userUUID", data.user_id);
        toast.success("Login successful!");
        navigate("/patient-dashboard");
      } else if (data.user_exists === false) {
        toast.info("New user detected. Please register to continue.");
        setTimeout(() => {
          navigate("/register", { state: { phone: phone } });
        }, 1500); // Slight delay to allow toast to be visible
      } else {
        throw new Error(data.error || "Unexpected response from server");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setErrorVal(true);
      if (error.code === "auth/invalid-verification-code") {
        toast.error("Invalid OTP. Please try again.");
      } else {
        toast.error(`An error occurred: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (value, country) => {
    setPhoneNumber(value);
    const digits = value.replace(country.dialCode, "");
    setDigitCount(digits.length);
    const digitsRequired = getRequiredDigits(country.countryCode);
    setRequiredDigits(digitsRequired);

    const isValid = validatePhoneNumber(value.replace(country.dialCode, ""), country.countryCode);
    if (!isValid) {
      setPhoneError(`Invalid phone number length for ${country.name}`);
    } else {
      setPhoneError("");
    }
  };

  const handleOtpChange = (otp) => {
    const numericOtp = otp.replace(/\D/g, "");
    setOtp(numericOtp);

    if (numericOtp.length !== 6 && verificationAttempted) {
      setVerificationAttempted(false);
    }
  };

  const handleEditClick = () => {
    setIsOtpSent(false);
    setOtp("");
    setVerificationId("");
    setTimer(60);
    setCanResend(false);
    setErrorVal(false);
  };

  return (
    <>
      <ToastContainer />
      <div id="recaptcha-container"></div>
      <div className="main-wrapper login-body">
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col-lg-6 login-wrap">
              <div className="login-sec">
                <div className="log-img">
                  <img className="img-fluid" src={login02} alt="#" />
                </div>
              </div>
            </div>
            <div className="col-lg-6 login-wrap-bg">
              <div className="login-wrapper">
                <div className="loginbox">
                  <div className="login-right">
                    <div className="login-right-wrap">
                      <div className="account-logo">
                        <Link to="/patient-dashboard">
                          <img src={loginlogo} height={60} alt="#" />
                        </Link>
                      </div>
                      <h2>{isOtpSent ? "Verify OTP" : "Login"}</h2>
                      {!isOtpSent ? (
                        <form onSubmit={sendOtp}>
                          <div className="form-group">
                            <PhoneInput
                              onlyCountries={["in", "gb", "us", "ae", "ng", "lk", "bd"]}
                              country="in"
                              value={phoneNumber}
                              onChange={handlePhoneChange}
                              className="phoneinput"
                              placeholder="Enter phone number"
                              required
                              inputProps={{
                                required: true,
                              }}
                              countryCodeEditable={false}
                              masks={{
                                in: "..........",
                                gb: "..........",
                                us: "..........",
                                ae: ".........",
                                ng: "...........",
                                lk: ".........",
                                bd: "..........",
                              }}
                            />
                            <label>
                              Phone Number <span className="login-danger">*</span>
                            </label>
                            <div className="mt-2 d-flex justify-content-between">
                              {phoneError && <div className="text-danger">{phoneError}</div>}
                              {errorVal && (
                                <div className="text-danger">Failed to send OTP. Please try again.</div>
                              )}
                              <div></div>
                              {digitCount}/{requiredDigits}
                            </div>
                          </div>
                          <div className="form-group login-btn">
                            <button
                              className="btn btn-primary btn-block"
                              type="submit"
                              disabled={loading || phoneError || digitCount !== requiredDigits}
                            >
                              {loading ? "Sending..." : "Send OTP"}
                            </button>
                          </div>
                        </form>
                      ) : (
                        <form onSubmit={verifyOtp}>
                          <div className="form-group">
                            <label>
                              Enter OTP <span className="login-danger">*</span>
                            </label>
                            <div className="d-flex align-items-center">
                              <OTPInput
                                value={otp}
                                onChange={handleOtpChange}
                                numInputs={6}
                                renderInput={(props) => (
                                  <input
                                    {...props}
                                    style={{
                                      width: "3rem",
                                      height: "3rem",
                                      margin: "0 0.5rem",
                                      fontSize: "1.5rem",
                                      borderRadius: "4px",
                                      border: "1px solid #ccc",
                                      textAlign: "center",
                                    }}
                                  />
                                )}
                                separator={<span> </span>}
                                isInputNum
                                shouldAutoFocus
                              />
                              <button
                                type="button"
                                className="btn btn-link p-0 ml-2"
                                onClick={handleEditClick}
                              >
                                <i className="feather-edit"></i>
                              </button>
                            </div>
                            <p className="mt-2">Please check OTP SMS on: +{phoneNumber}</p>
                            {errorVal && (
                              <div className="text-danger mt-2">
                                Failed to verify OTP. Please try again.
                              </div>
                            )}
                          </div>
                          <div className="form-group login-btn">
                            <button
                              className="btn btn-primary btn-block"
                              type="submit"
                              disabled={loading || otp.length !== 6}
                            >
                              {loading ? "Verifying..." : "Verify OTP"}
                            </button>
                          </div>
                          <div className="form-group text-center">
                            {canResend ? (
                              <button
                                className="btn btn-link"
                                type="button"
                                onClick={sendOtp}
                                disabled={loading}
                              >
                                Resend OTP
                              </button>
                            ) : (
                              <p>Resend OTP in {timer} seconds</p>
                            )}
                          </div>
                        </form>
                      )}
                      <div className="next-sign">
                        {/* <p className="account-subtitle">
                          Need an account? <Link to="/signup">Sign Up</Link>
                        </p> */}
                        <div className="social-login">
                          <Link to="#">
                            <img src={loginicon01} alt="#" />
                          </Link>
                          <Link to="#">
                            <img src={loginicon02} alt="#" />
                          </Link>
                          <Link to="#">
                            <img src={loginicon03} alt="#" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;